import type { ConsoleOverview } from '@plato/shared';
import { Field } from '../../components/field.tsx';
import { formatCount } from '../../components/format.ts';
import { Loaded, Page } from '../../components/page.tsx';
import { useTenantQuery } from '../../data/console-context.tsx';
import type { ConsoleData, TenantSummary } from '../../data/console-data.ts';
import { formatCredits, formatPercent } from '../usage/format.ts';
import { LimitMeter } from '../usage/limit-meter.tsx';
import '../usage/usage.css';

const loadOverview = (data: ConsoleData, tenant: TenantSummary) => data.getOverview(tenant.id);

export function OverviewSection() {
  const overview = useTenantQuery(loadOverview);
  return (
    <Page
      title="Overview"
      summary="This month so far (UTC): credits, conversations, ratings and errors."
    >
      <Loaded query={overview}>{(value) => <OverviewReport overview={value} />}</Loaded>
    </Page>
  );
}

function OverviewReport({ overview }: { overview: ConsoleOverview }) {
  const rated = overview.conversations_rated_up + overview.conversations_rated_down;
  return (
    <>
      <div className="hero">
        <span className="hero-label">Credits used this month</span>
        <span className="hero-value">{formatCredits(overview.credit_micros)}</span>
      </div>
      <dl className="stats">
        <Field label="Conversations" value={formatCount(overview.conversations)} />
        <Field label="Replies" value={formatCount(overview.runs)} />
        <Field
          label="Thumbs up, of rated conversations"
          value={
            rated === 0 ? 'No ratings yet' : formatPercent(overview.conversations_rated_up / rated)
          }
        />
        <Field
          label="Conversations with an error"
          value={formatCount(overview.conversations_with_errors)}
        />
        <Field
          label="Tool calls that failed"
          value={`${formatCount(overview.failed_tool_calls)} of ${formatCount(overview.tool_calls)}`}
        />
      </dl>
      <nav className="overview-links" aria-label="Look closer">
        <a className="button" href="#/conversations">
          Review conversations
        </a>
        <a className="button" href="#/usage">
          Usage and client limits
        </a>
      </nav>
      <section aria-labelledby="near-limit">
        <h2 id="near-limit">Clients near their limit</h2>
        {overview.clients_near_limit.length === 0 ? (
          <p className="muted">
            No client has used {formatPercent(overview.near_limit_ratio)} of its monthly credits.
          </p>
        ) : (
          <table>
            <caption className="visually-hidden">Clients near their limit</caption>
            <thead>
              <tr>
                <th scope="col">Client</th>
                <th scope="col">Plan</th>
                <th scope="col">Used</th>
              </tr>
            </thead>
            <tbody>
              {overview.clients_near_limit.map((client) => (
                <tr key={client.organization ?? '(none)'}>
                  <td>{client.organization}</td>
                  <td>{client.plan ?? '—'}</td>
                  <td>
                    <LimitMeter client={client} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </>
  );
}
